"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.initializeEngine = void 0;
const common_1 = require("@finley-technologies/common");
const formulaEngineUtils = __importStar(require("../utils"));
const ast_1 = require("./ast");
const pick_1 = __importDefault(require("lodash/pick"));
/**
 * Usage:
 * 1. Caller initializes engine with list of formulas
 * 2. Engine displays if range variables are required
 * 3. If range variables are required, caller must provide full data set (addData function - stream compatible)
 *    -  When adding data to engine, engine will only keep required properties for range variable evaluation
 * 4. Engine can be reset to clear all memory
 * 5. Engine can run evaluation on individual items
 *    a) For cacheable results, retrieve cache if already populated, otherwise evaluate and update cache
 */
const initializeEngine = (options) => {
    const initialFormulas = options?.formulas ?? [];
    const rangeVariablesMap = new Map();
    const rowVariablesSet = new Set();
    const formulaStore = new Map();
    // TODO: @sachinr4 to revert try/catch and allow failure at time of building formulas
    // This is prevented for now and allowed to raise an error at time of input evaluation while engine is still in comparison phase with engine adapters
    // Catching the error here allows other formulas in this initial set of formulas to continue evaluating fully for comparison purposes
    for (const formula of initialFormulas) {
        try {
            // Build function evaluator
            const formulaEvaluator = (0, ast_1.buildFunction)({ formula });
            // If any range values are required, initialize range value map with empty array
            formulaEvaluator.rangeVariables.forEach((variable) => rangeVariablesMap.set(variable, []));
            formulaEvaluator.rowVariables.forEach((variable) => rowVariablesSet.add(variable));
            formulaStore.set(formula, formulaEvaluator);
        }
        catch (error) {
            console.log(`[FormulaEngine TEST] Failure to build formula: ${formula}`, {
                stringifiedError: JSON.stringify(error),
            });
        }
    }
    // Cached after all formulas have been built and required range value map is initialized
    // Used with `addRangeDataRow` function
    const rangeVariables = Array.from(rangeVariablesMap.keys());
    // Retrieve standard variable name corresponding to range variable
    const rowVariablesFromRangeVariables = rangeVariables.map((variable) => formulaEngineUtils.extractVariableNameFromRange(variable));
    // Collect full list of require variables (to be used when sanitizing range data output)
    const requiredVariables = [
        ...Array.from(rowVariablesSet),
        ...rowVariablesFromRangeVariables,
    ];
    const rangeDataRequired = rangeVariables.length > 0;
    return {
        rangeDataRequired,
        // How many passes are required?
        // first pass: collect range values as arrays and return sanitized data set
        // no additional evaluation needed if no row variables are present (result will be cached)
        // if row variables are present, cache cannot be used
        sanitizeRangeData: ({ row }) => {
            rangeVariables.forEach((rangeVariable) => {
                const rangeValues = rangeVariablesMap.get(rangeVariable);
                // Always throw if not built at initialization
                // Range based formulas should not be built at time of formula evaluation because the initial evaluation will not have the required range value
                if (!rangeValues) {
                    throw new Error(`Range variable ${rangeVariable} was not initialized with formulas`);
                }
                // Extract property and set in rangeValues map
                const property = formulaEngineUtils.getProperty(row, rangeVariable, {
                    rangeVariable: true,
                });
                if (property.error) {
                    throw new Error(`Missing range variable ${rangeVariable} in input`);
                }
                rangeValues.push(property.value);
            });
            return (0, pick_1.default)(row, requiredVariables);
        },
        evaluate: ({ formula, formulaInput }) => {
            const cachedEvaluator = formulaStore.get(formula);
            let formulaEvaluator;
            if (cachedEvaluator) {
                formulaEvaluator = cachedEvaluator;
            }
            else if (options?.dynamicEvaluation) {
                // Build and cache
                formulaEvaluator = (0, ast_1.buildFunction)({ formula });
                formulaStore.set(formula, formulaEvaluator);
            }
            else {
                // Default: not allowed to build during evaluation
                throw new Error('Formula not built');
            }
            const { node, inputs, output } = formulaEvaluator.evaluate({
                rowVariables: formulaInput,
                rangeVariables: rangeVariablesMap,
            });
            (0, common_1.finleyAssert)(output.error === null, `Formula evaluation was unsuccessful for formula: ${formula}. Error: ${output.error}`);
            return {
                node,
                inputs,
                output,
            };
        },
    };
};
exports.initializeEngine = initializeEngine;
//# sourceMappingURL=index.js.map